/**
 * ReceiveOrderFormDialog — New / Edit for Sales Control › Release Order (Receive
 * Order). Converted 1:1 from Access `frmCtrlSalesQryROAdd_upgrade` (header form)
 * + `..._subForm` (releasable container datasheet picker).
 *
 * Header fields (Access Left/Top): customer_invoice_no (combo, drives suggest +
 * container load), release_no (auto-suggested text), release_date (date),
 * send_to_warehouse (combo code+company_name), wh_range (text), note (multiline),
 * wh_storage_deadline (date). Labels per Access: "Customer INV", "Release No.",
 * "Date", "Send to WH", "WH Range", "Note", "WH Storage".
 *
 * Cascade (Access InvChange): pick customer_invoice_no → Suggest_IndexName fills
 * release_no (FR{NN}/{invoice}) + load releasable containers into the picker grid.
 *
 * Container picker (Access subForm + tick_all_Click): user ticks rows; "tick all"
 * fills nw_release=nw_releasable / gw / bag for checked rows. nw/gw/bag_release
 * editable per row. Only checked rows go into details[].
 *
 * Edit mode (Access btnEdit): customer_invoice_no + release_no DISABLED; hydrate
 * header from GET /{id}/form; existing detail rows re-mark + fill the picker.
 *
 * Save validation (Access btnSave, verbatim EN): New duplicate release_no →
 * "Release No. is existed" (BE 422); invoice empty → "Customer Invoice No. is
 * required"; release_no empty → "Release No. is required"; date empty →
 * "Release Date is required".
 */
import { useEffect, useRef, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog,
  DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type ReleasableContainer,
  type RoLookupItem,
  type SaleReleaseOrderWrite,
  useCreateSaleReleaseOrderMutation,
  useUpdateSaleReleaseOrderMutation,
  useLazyGetSaleReleaseOrderFormQuery,
  useLazySuggestReleaseNoQuery,
  useLazyCheckReleaseNoQuery,
  useLazyGetReleaseOrderDetailsQuery,
  useLazyGetReleasableContainersQuery,
  useRoLookupCustomerInvoicesQuery,
  useRoLookupSendToWarehouseQuery,
} from '../../store/api/saleReleaseOrderApi'
import { WIDE_SCROLL_WRAPPER } from '../../theme/responsive'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

type Mode = 'new' | 'edit'

interface Props {
  open: boolean
  mode: Mode
  roId?: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  customerInvoiceNo: string
  releaseNo: string
  releaseDate: string
  sendToWarehouse: string
  whRange: string
  note: string
  whStorageDeadline: string
}
const EMPTY: Draft = {
  customerInvoiceNo: '', releaseNo: '', releaseDate: '', sendToWarehouse: '',
  whRange: '', note: '', whStorageDeadline: '',
}

/** Picker row = releasable container + editable release qty + tick. */
interface PickRow {
  key: string
  invoiceNo: string
  deliveryOrder: string
  containerNo: string
  nwCont: number | null
  nwReleasable: number | null
  gwReleasable: number | null
  bagReleasable: number | null
  checked: boolean
  nwRelease: string
  gwRelease: string
  bagRelease: string
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
}
// Header 2-col grid (Access 2664/8880/15108 columns collapse to 2 usable cols
// once the readonly DSum snapshot fields — INV Total NW/GW, Releasable NW/GW,
// Release NW/GW — are dropped; those are computed, FE gets them from BE views).
const grid2Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const numCellProps = { style: { textAlign: 'right' as const, fontSize: '0.75rem' }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
/** Access Format="Standard" → `#,##0.00`, right-aligned. */
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const str = (v: unknown): string => (v == null ? '' : String(v))

function withCurrent(options: RoLookupItem[], value: string): RoLookupItem | null {
  if (!value) return null
  return options.find((o) => o.value === value) ?? { value, label: value }
}

/* ───────── Reusable lookup combo (flat "<CODE> — <NAME>", collapse when equal) ───────── */
function Combo(props: {
  label: string
  value: string
  options: RoLookupItem[]
  loading?: boolean
  required?: boolean
  disabled?: boolean
  labelOnly?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string) => void
  onBlur?: () => void
  error?: boolean
  helperText?: string
}) {
  const { label, value, options, loading, required, disabled, labelOnly, onInputChange, onPick, onBlur, error, helperText } = props
  const sel = withCurrent(options, value)
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  return (
    <Autocomplete
      size={SM}
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '')}
      onInputChange={onInputChange ? (_e, v, reason) => { if (reason === 'input') onInputChange(v) } : undefined}
      getOptionLabel={(o) => (!o ? '' : labelOnly || o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => (
        <TextField {...params} label={required ? `${label} *` : label} onBlur={onBlur} error={error} helperText={helperText} />
      )}
    />
  )
}

export default function ReceiveOrderFormDialog({ open, mode, roId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [rows, setRows] = useState<PickRow[]>([])
  const [tickAll, setTickAll] = useState(false)
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)
  const [validationError, setValidationError] = useState('')
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [loadForm] = useLazyGetSaleReleaseOrderFormQuery()
  const [suggestReleaseNo, suggestState] = useLazySuggestReleaseNoQuery()
  const [checkReleaseNo] = useLazyCheckReleaseNoQuery()
  const [getDetails] = useLazyGetReleaseOrderDetailsQuery()
  const [getContainers] = useLazyGetReleasableContainersQuery()
  const [createRo, { isLoading: creating }] = useCreateSaleReleaseOrderMutation()
  const [updateRo, { isLoading: updating }] = useUpdateSaleReleaseOrderMutation()
  const saving = creating || updating

  const [invQ, setInvQ] = useState('')
  const [whQ, setWhQ] = useState('')
  const skip = { skip: !open }
  const invoices = useRoLookupCustomerInvoicesQuery(invQ || undefined, skip)
  const warehouses = useRoLookupSendToWarehouseQuery(whQ || undefined, skip)

  const isEdit = mode === 'edit'

  /** Build picker rows from releasable containers; merge existing details (Edit). */
  const buildRows = (containers: ReleasableContainer[], details?: { containerNo: string | null; nwRelease: number | null; gwRelease: number | null; bagRelease: number | null }[]) => {
    const detMap = new Map<string, { nwRelease: number | null; gwRelease: number | null; bagRelease: number | null }>()
    for (const d of details ?? []) detMap.set(str(d.containerNo), { nwRelease: d.nwRelease, gwRelease: d.gwRelease, bagRelease: d.bagRelease })
    return containers.map<PickRow>((c) => {
      const det = detMap.get(str(c.container_no))
      return {
        key: str(c.container_no) || String(c.id),
        invoiceNo: str(c.invoice_no),
        deliveryOrder: str(c.delivery_order),
        containerNo: str(c.container_no),
        nwCont: c.nw_cont,
        nwReleasable: c.nw_releasable,
        gwReleasable: c.gw_releasable,
        bagReleasable: c.bag_releasable,
        checked: !!det,
        nwRelease: det ? fmtStd(det.nwRelease) : '',
        gwRelease: det ? fmtStd(det.gwRelease) : '',
        bagRelease: det ? fmtStd(det.bagRelease) : '',
      }
    })
  }

  const loadContainers = async (invoiceNo: string, releaseNoForDetails?: string) => {
    if (!invoiceNo) { setRows([]); return }
    const containers = await getContainers(invoiceNo).unwrap().catch(() => [] as ReleasableContainer[])
    let details: { containerNo: string | null; nwRelease: number | null; gwRelease: number | null; bagRelease: number | null }[] | undefined
    if (releaseNoForDetails) {
      const res = await getDetails(releaseNoForDetails).unwrap().catch(() => null)
      details = res?.items
    }
    setRows(buildRows(containers, details))
    setTickAll(false)
  }

  // Reset transient state on open
  useEffect(() => {
    if (!open) return
    setRegenNotice(null)
    setValidationError('')
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    setTickAll(false)
  }, [open, mode, roId])

  // Hydrate on open. Deps limited to [open, mode, roId] (skill access-suggest #4).
  useEffect(() => {
    if (!open) return
    let cancelled = false
    void (async () => {
      if (mode === 'new') {
        if (cancelled) return
        setDraft({ ...EMPTY, releaseDate: dayjs().format('YYYY-MM-DD') })
        setRows([])
        return
      }
      if (!roId) return
      const f = await loadForm(roId).unwrap().catch(() => null)
      if (cancelled || !f) return
      const g = (k: string) => str((f as Record<string, unknown>)[k])
      const invoiceNo = g('customerInvoiceNo')
      const releaseNo = g('releaseNo')
      setDraft({
        customerInvoiceNo: invoiceNo,
        releaseNo,
        releaseDate: g('releaseDate').slice(0, 10),
        sendToWarehouse: g('sendToWarehouse'),
        whRange: g('whRange'),
        note: g('note'),
        whStorageDeadline: g('whStorageDeadline').slice(0, 10),
      })
      await loadContainers(invoiceNo, releaseNo)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, roId])

  // Dismiss regen alert when user edits release_no away from suggested value.
  useEffect(() => {
    if (regenNotice && draft.releaseNo !== regenNotice.to) setRegenNotice(null)
  }, [draft.releaseNo, regenNotice])

  /** Access InvChange: pick invoice → suggest release_no + reload container picker. */
  const handleInvoicePick = async (invoiceNo: string) => {
    setDraft((p) => ({ ...p, customerInvoiceNo: invoiceNo, releaseNo: '' }))
    setRegenNotice(null)
    if (!invoiceNo) { setRows([]); return }
    const [suggest] = await Promise.all([
      suggestReleaseNo(invoiceNo, false).unwrap().catch(() => null),
      loadContainers(invoiceNo),
    ])
    const rn = (suggest?.releaseNo || '').trim()
    if (rn) setDraft((p) => ({ ...p, releaseNo: rn }))
  }

  /** Access btnSave errCheck 1 fast path: blur release_no → check duplicate → re-suggest. */
  const handleReleaseNoBlur = async () => {
    if (isEdit) return
    const val = draft.releaseNo.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await checkReleaseNo({ releaseNo: val }).unwrap()
      if (!check.exists) return
      const invoiceNo = draft.customerInvoiceNo.trim()
      if (!invoiceNo) return
      const next = await suggestReleaseNo(invoiceNo, false).unwrap()
      const newVal = (next.releaseNo || '').trim()
      if (newVal && newVal !== val) {
        setDraft((p) => ({ ...p, releaseNo: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE raises 422 at save if it truly duplicates — don't poison UX here.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  /* ───────── Picker row mutations ───────── */
  const setRow = (key: string, patch: Partial<PickRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))

  const toggleRow = (key: string, checked: boolean) =>
    setRows((prev) => prev.map((r) => {
      if (r.key !== key) return r
      // On tick (Access tick_all parity per-row): prefill release = releasable if empty.
      if (checked) return {
        ...r, checked: true,
        nwRelease: r.nwRelease || fmtStd(r.nwReleasable),
        gwRelease: r.gwRelease || fmtStd(r.gwReleasable),
        bagRelease: r.bagRelease || fmtStd(r.bagReleasable),
      }
      return { ...r, checked: false }
    }))

  // Access tick_all_Click: fill nw/gw/bag from releasable for all checked; clear when unticked.
  const handleTickAll = (checked: boolean) => {
    setTickAll(checked)
    setRows((prev) => prev.map((r) => (checked
      ? { ...r, checked: true, nwRelease: fmtStd(r.nwReleasable), gwRelease: fmtStd(r.gwReleasable), bagRelease: fmtStd(r.bagReleasable) }
      : { ...r, checked: false, nwRelease: '', gwRelease: '', bagRelease: '' })))
  }

  const invoiceEmpty = !draft.customerInvoiceNo.trim()
  const releaseNoEmpty = !draft.releaseNo.trim()
  const dateEmpty = !draft.releaseDate.trim()
  const canSave = canEdit && !invoiceEmpty && !releaseNoEmpty && !dateEmpty && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (precheckBusyRef.current) return // race guard (blur + Save same tick)
    setValidationError('')
    // Access btnSave errCheck 2/3/4 (verbatim EN)
    if (invoiceEmpty) { setValidationError('Customer Invoice No. is required'); return }
    if (releaseNoEmpty) { setValidationError('Release No. is required'); return }
    if (dateEmpty) { setValidationError('Release Date is required'); return }
    if (!canSave) return

    const payload: SaleReleaseOrderWrite = {
      customerInvoiceNo: draft.customerInvoiceNo || null,
      releaseNo: draft.releaseNo || null,
      releaseDate: draft.releaseDate || null,
      sendToWarehouse: draft.sendToWarehouse || null,
      whStorageDeadline: draft.whStorageDeadline || null,
      whRange: draft.whRange || null,
      note: draft.note || null,
      details: rows.filter((r) => r.checked).map((r) => ({
        invoiceNo: r.invoiceNo || null,
        deliveryOrder: r.deliveryOrder || null,
        containerNo: r.containerNo || null,
        nwRelease: parseNum(r.nwRelease),
        gwRelease: parseNum(r.gwRelease),
        bagRelease: parseNum(r.bagRelease),
      })),
    }
    try {
      if (isEdit && roId) {
        await updateRo({ id: roId, body: payload }).unwrap()
        snack.success(`Release Order ${draft.releaseNo} updated`)
      } else {
        await createRo(payload).unwrap()
        snack.success(`Release Order ${draft.releaseNo} added`)
      }
      onSaved()
      onClose()
    } catch (err) {
      const msg = getErrorMessage(err, 'Save release order failed')
      // Access btnSave errCheck 1: New + duplicate release_no → BE 422.
      if (!isEdit && /exist|duplicate/i.test(msg)) { setValidationError('Release No. is existed'); return }
      snack.error(msg)
    }
  }

  const title = isEdit ? 'Edit' : 'New'

  const dateField = (key: keyof Draft, label: string, required?: boolean) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, required, InputLabelProps: { shrink: true } } }}
    />
  )

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>{title} — Release Order</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit release orders.</Alert>}
        {regenNotice && (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Release No. <b>{regenNotice.from}</b> was just created — suggested new code <b>{regenNotice.to}</b>.
          </Alert>
        )}
        {validationError && <Alert severity="error" sx={{ mb: 1.25, py: 0.25 }} onClose={() => setValidationError('')}>{validationError}</Alert>}

        {/* ── Header ── */}
        <Typography sx={sectionTitleSx}>Release order information</Typography>
        <Box sx={grid2Sx}>
          <Combo label="Customer INV" value={draft.customerInvoiceNo} required disabled={isEdit} labelOnly
            options={invoices.data?.items ?? []} loading={invoices.isFetching} onInputChange={setInvQ}
            error={invoiceEmpty} onPick={(v) => void handleInvoicePick(v)} />
          <TextField
            label="Release No. *" size={SM} fullWidth value={draft.releaseNo}
            disabled={isEdit}
            onChange={(e) => set('releaseNo', e.target.value)}
            onBlur={() => void handleReleaseNoBlur()}
            error={releaseNoEmpty}
            InputLabelProps={{ shrink: true }}
            helperText={
              isEdit ? 'Release No. cannot be changed after creation.'
                : precheckBusy ? 'Verifying Release No.…'
                : suggestState.isFetching ? 'Suggesting next code…'
                : 'Auto-suggested from Customer INV; edit if needed.'
            }
          />
          {dateField('releaseDate', 'Date', true)}
          <Combo label="Send to WH" value={draft.sendToWarehouse}
            options={warehouses.data?.items ?? []} loading={warehouses.isFetching} onInputChange={setWhQ}
            onPick={(v) => set('sendToWarehouse', v)} />
          <TextField label="WH Range" size={SM} fullWidth value={draft.whRange}
            onChange={(e) => set('whRange', e.target.value)} />
          {dateField('whStorageDeadline', 'WH Storage')}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <TextField label="Note" size={SM} fullWidth multiline minRows={2} value={draft.note}
              onChange={(e) => set('note', e.target.value)} />
          </Box>
        </Box>

        {/* ── Release details (Access subForm container picker) ── */}
        <Typography sx={sectionTitleSx}>Release details</Typography>
        {invoiceEmpty ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Choose a Customer INV to load releasable containers.
          </Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No releasable containers.</Typography>
        ) : (
          <Box sx={WIDE_SCROLL_WRAPPER}>
            <Box sx={{ minWidth: 1000 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '44px 1.3fr 1.1fr 1.3fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr', columnGap: 0.75, alignItems: 'center', px: 0.5, py: 0.5, borderBottom: '2px solid', borderColor: 'divider', fontWeight: 700, fontSize: '0.72rem' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Checkbox size="small" checked={tickAll} disabled={!canEdit}
                    onChange={(e) => handleTickAll(e.target.checked)} sx={{ p: 0 }} title="Tick all" />
                </Box>
                <Box>Container No</Box>
                <Box>Delivery Order</Box>
                <Box>Invoice No</Box>
                <Box sx={{ textAlign: 'right' }}>Total NW</Box>
                <Box sx={{ textAlign: 'right' }}>Releasable NW</Box>
                <Box sx={{ textAlign: 'right' }}>Releasable GW</Box>
                <Box sx={{ textAlign: 'right' }}>Releasable BAG</Box>
                <Box sx={{ textAlign: 'right' }}>Release NW</Box>
                <Box sx={{ textAlign: 'right' }}>Release GW</Box>
                <Box sx={{ textAlign: 'right' }}>Release BAG</Box>
              </Box>
              {rows.map((r) => (
                <Box key={r.key} sx={{ display: 'grid', gridTemplateColumns: '44px 1.3fr 1.1fr 1.3fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr', columnGap: 0.75, alignItems: 'center', px: 0.5, py: 0.25, borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.75rem' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox size="small" checked={r.checked} disabled={!canEdit}
                      onChange={(e) => toggleRow(r.key, e.target.checked)} sx={{ p: 0 }} />
                  </Box>
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.containerNo}>{r.containerNo}</Box>
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.deliveryOrder}>{r.deliveryOrder}</Box>
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.invoiceNo}>{r.invoiceNo}</Box>
                  <Box sx={{ textAlign: 'right' }}>{fmtStd(r.nwCont)}</Box>
                  <Box sx={{ textAlign: 'right' }}>{fmtStd(r.nwReleasable)}</Box>
                  <Box sx={{ textAlign: 'right' }}>{fmtStd(r.gwReleasable)}</Box>
                  <Box sx={{ textAlign: 'right' }}>{fmtStd(r.bagReleasable)}</Box>
                  <TextField variant="standard" fullWidth value={r.nwRelease} disabled={!canEdit}
                    onChange={(e) => setRow(r.key, { nwRelease: e.target.value })}
                    onBlur={() => setRow(r.key, { nwRelease: fmtStd(r.nwRelease) })}
                    inputProps={numCellProps} />
                  <TextField variant="standard" fullWidth value={r.gwRelease} disabled={!canEdit}
                    onChange={(e) => setRow(r.key, { gwRelease: e.target.value })}
                    onBlur={() => setRow(r.key, { gwRelease: fmtStd(r.gwRelease) })}
                    inputProps={numCellProps} />
                  <TextField variant="standard" fullWidth value={r.bagRelease} disabled={!canEdit}
                    onChange={(e) => setRow(r.key, { bagRelease: e.target.value })}
                    onBlur={() => setRow(r.key, { bagRelease: fmtStd(r.bagRelease) })}
                    inputProps={numCellProps} />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
